import { CAPTURE_JPEG_QUALITY, CAPTURE_MAX_EDGE } from '@room-verify/shared';
import {
  ATTACH_TIMEOUT_MS,
  MAX_ATTACH_ATTEMPTS,
  MIN_READY_STATE,
  canRetryAttach,
  constraintsForAttempt,
  describeCamera,
  hasUsableDimensions,
  pickRearCamera,
  type CameraDiagnostics,
  type VideoSnapshot,
} from './camera-lifecycle';

/**
 * Live camera capture.
 *
 * The supervisor app has no file input anywhere — not hidden, not disabled. Frames come
 * only from getUserMedia, so a photo cannot be chosen from the gallery. That constraint
 * is the whole point of the product: the evidence has to have been taken at the room.
 *
 * The lifecycle here is built around one hard-won rule: **a resolved getUserMedia promise
 * is not a working camera.** Nothing is reported as live until the video element has
 * decoded a frame with real dimensions.
 */

export type CameraFailure =
  | 'DENIED'
  | 'NOT_FOUND'
  | 'INSECURE'
  | 'UNAVAILABLE'
  | 'NO_FRAMES'
  | 'UNKNOWN';

export class CameraError extends Error {
  constructor(
    readonly reason: CameraFailure,
    readonly diagnostics?: CameraDiagnostics,
  ) {
    super(reason);
    this.name = 'CameraError';
  }
}

/** getUserMedia needs a secure context; `localhost` counts as one. */
export const cameraSupported = (): boolean =>
  typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);

export const secureContextOk = (): boolean =>
  typeof window !== 'undefined' &&
  (window.isSecureContext || location.hostname === 'localhost' || location.hostname === '127.0.0.1');

/** Standalone home-screen PWA, where iOS is at its most aggressive about suspending. */
export const isStandalone = (): boolean =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true);

/* ------------------------------------------------------------- diagnostics */

const DEBUG_KEY = 'roomverify.cameraDebug';

/** Opt-in field diagnostics: `localStorage.setItem('roomverify.cameraDebug','1')`. */
const debugEnabled = (): boolean => {
  if (import.meta.env.DEV) return true;
  try {
    return localStorage.getItem(DEBUG_KEY) === '1';
  } catch {
    return false;
  }
};

/** Never rendered — the supervisor sees plain recovery copy, not a device fingerprint. */
export function logCameraDiagnostics(event: string, diagnostics: CameraDiagnostics): void {
  if (!debugEnabled()) return;
  console.info(`[room-verify:camera] ${event}`, diagnostics);
}

export function snapshotVideo(
  video: HTMLVideoElement | null,
  stream: MediaStream | null,
): VideoSnapshot {
  const track = stream?.getVideoTracks()[0] ?? null;
  return {
    hasVideoElement: Boolean(video),
    hasStream: Boolean(stream),
    videoWidth: video?.videoWidth ?? 0,
    videoHeight: video?.videoHeight ?? 0,
    readyState: video?.readyState ?? 0,
    paused: video?.paused ?? true,
    trackReadyState: track ? (track.readyState as 'live' | 'ended') : null,
    trackMuted: track ? track.muted : null,
  };
}

export function cameraDiagnostics(
  video: HTMLVideoElement | null,
  stream: MediaStream | null,
  extra: { attempt?: number; outcome?: string } = {},
): CameraDiagnostics {
  const track = stream?.getVideoTracks()[0] ?? null;
  let settings: Record<string, unknown> | null = null;
  try {
    settings = track?.getSettings() ? { ...track.getSettings() } : null;
  } catch {
    settings = null;
  }
  return describeCamera({
    snapshot: snapshotVideo(video, stream),
    trackLabel: track?.label ?? null,
    settings,
    userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
    standalone: isStandalone(),
    isSecureContext: secureContextOk(),
    cameraSupported: cameraSupported(),
    ...extra,
  });
}

/* --------------------------------------------------------------- acquire */

function toCameraError(error: unknown): CameraError {
  const name = (error as DOMException)?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') return new CameraError('DENIED');
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return new CameraError('NOT_FOUND');
  }
  if (name === 'NotReadableError' || name === 'AbortError') {
    return new CameraError('UNAVAILABLE');
  }
  return new CameraError('UNKNOWN');
}

/**
 * Requests a stream with constraints that get progressively less demanding.
 *
 * Attempt 0 asks only for the rear camera and pins no resolution — see
 * `constraintsForAttempt` for why a resolution on the first attempt is what produces a
 * black stream on iPhone.
 */
export async function acquireCamera(attempt: number): Promise<MediaStream> {
  if (!secureContextOk()) throw new CameraError('INSECURE');
  if (!cameraSupported()) throw new CameraError('UNAVAILABLE');

  let rearDeviceId: string | undefined;
  if (attempt >= 2) {
    try {
      // Labels are only populated after permission has been granted, which by this
      // attempt it has been.
      const devices = await navigator.mediaDevices.enumerateDevices();
      rearDeviceId = pickRearCamera(devices);
    } catch {
      rearDeviceId = undefined;
    }
  }

  try {
    return await navigator.mediaDevices.getUserMedia(constraintsForAttempt(attempt, rearDeviceId));
  } catch (error) {
    throw toCameraError(error);
  }
}

export function stopCamera(stream: MediaStream | null): void {
  stream?.getTracks().forEach((track) => {
    try {
      track.stop();
    } catch {
      /* a track that is already gone is not a problem */
    }
  });
}

/** Fully detaches a stream from an element so a retry starts from a clean pipeline. */
export function detachStream(video: HTMLVideoElement | null): void {
  if (!video) return;
  try {
    video.pause();
  } catch {
    /* pausing a never-started element can throw on some engines */
  }
  video.srcObject = null;
  video.removeAttribute('src');
}

/* ---------------------------------------------------------------- attach */

export type AttachOutcome =
  | { ok: true; width: number; height: number }
  | { ok: false; reason: 'TIMEOUT' | 'NO_TRACK' | 'PLAY_BLOCKED'; playError?: string };

/**
 * Attaches a stream to a video element and waits until it genuinely produces a picture.
 *
 * iOS specifics this handles, each of which alone produces a permanently black element:
 *
 * 1. **Attributes, not just properties.** WebKit decides autoplay eligibility partly from
 *    the `muted` and `playsinline` *attributes*. React sets `muted` as a property only, so
 *    an element that looks muted in JSX can still be treated as unmuted by iOS and have
 *    its `play()` rejected.
 * 2. **`play()` must be called explicitly.** `autoplay` fires on `src` changes; assigning
 *    `srcObject` does not reliably trigger it.
 * 3. **Events are not enough.** `loadedmetadata` can fire while `videoWidth` is still 0,
 *    and on some iOS builds it never fires at all — so the wait polls dimensions as well
 *    as listening, and dimensions are what decide.
 */
export async function attachStreamToVideo(
  video: HTMLVideoElement,
  stream: MediaStream,
  { timeoutMs = ATTACH_TIMEOUT_MS }: { timeoutMs?: number } = {},
): Promise<AttachOutcome> {
  if (stream.getVideoTracks().length === 0) return { ok: false, reason: 'NO_TRACK' };

  // Clear any previous pipeline before assigning — reusing an element that still holds
  // a suspended stream is one of the ways iOS ends up showing black.
  if (video.srcObject && video.srcObject !== stream) detachStream(video);

  video.muted = true;
  video.defaultMuted = true;
  video.setAttribute('muted', '');
  video.playsInline = true;
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
  video.autoplay = true;
  video.setAttribute('autoplay', '');

  video.srcObject = stream;

  let playError: string | undefined;

  /**
   * Deliberately never awaited.
   *
   * `play()` resolves when playback actually begins, so on a stream that produces no
   * frames the promise simply never settles — awaiting it hangs the whole attach and
   * turns a black viewfinder into a permanent "Starting camera…". Frames are the only
   * arbiter of readiness; the play promise is used solely to capture a rejection reason.
   */
  const tryPlay = (): void => {
    try {
      const promise = video.play() as Promise<void> | undefined;
      promise?.then(
        () => {
          playError = undefined;
        },
        (error: unknown) => {
          playError = (error as DOMException)?.name ?? 'PlayError';
        },
      );
    } catch (error) {
      playError = (error as DOMException)?.name ?? 'PlayError';
    }
  };

  tryPlay();

  const ready = await waitForFrames(video, timeoutMs, tryPlay);

  if (ready) return { ok: true, width: video.videoWidth, height: video.videoHeight };
  if (playError) return { ok: false, reason: 'PLAY_BLOCKED', playError };
  return { ok: false, reason: 'TIMEOUT' };
}

/**
 * Resolves once the element has real dimensions, or false on timeout.
 *
 * Belt and braces on purpose: readiness events *and* a poll. On iOS either one alone has
 * been observed to miss — the events can arrive before dimensions exist, and the poll
 * alone would waste up to its interval on a fast device.
 */
function waitForFrames(
  video: HTMLVideoElement,
  timeoutMs: number,
  retryPlay: () => void,
): Promise<boolean> {
  if (hasUsableDimensions(video) && video.readyState >= MIN_READY_STATE) {
    return Promise.resolve(true);
  }

  return new Promise<boolean>((resolve) => {
    let settled = false;
    const events = ['loadedmetadata', 'loadeddata', 'canplay', 'playing', 'resize'] as const;

    const finish = (value: boolean) => {
      if (settled) return;
      settled = true;
      clearInterval(poll);
      clearTimeout(timer);
      events.forEach((event) => video.removeEventListener(event, onEvent));
      resolve(value);
    };

    const check = () => {
      if (hasUsableDimensions(video) && video.readyState >= MIN_READY_STATE) finish(true);
    };

    const onEvent = () => {
      // A paused element never produces frames; nudge it every time it signals progress.
      if (video.paused) retryPlay();
      check();
    };

    events.forEach((event) => video.addEventListener(event, onEvent));
    const poll = setInterval(check, 120);
    const timer = setTimeout(() => finish(false), timeoutMs);
    check();
  });
}

/* --------------------------------------------------------------- session */

export interface CameraSession {
  stream: MediaStream;
  width: number;
  height: number;
  attempt: number;
}

/**
 * Acquires a camera and proves it works, retrying with weaker constraints.
 *
 * Every failed attempt is fully torn down — element detached, tracks stopped — before the
 * next one. Reusing a half-dead pipeline is what turns one bad attempt into a permanently
 * black viewfinder.
 */
export async function startCameraSession(
  video: HTMLVideoElement,
  { maxAttempts = MAX_ATTACH_ATTEMPTS }: { maxAttempts?: number } = {},
): Promise<CameraSession> {
  let lastError: CameraError | null = null;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    let stream: MediaStream | null = null;
    try {
      stream = await acquireCamera(attempt);
      const outcome = await attachStreamToVideo(video, stream);

      if (outcome.ok) {
        logCameraDiagnostics(
          'attach_ok',
          cameraDiagnostics(video, stream, { attempt, outcome: 'ok' }),
        );
        return { stream, width: outcome.width, height: outcome.height, attempt };
      }

      logCameraDiagnostics(
        'attach_failed',
        cameraDiagnostics(video, stream, { attempt, outcome: outcome.reason }),
      );
      lastError = new CameraError(
        'NO_FRAMES',
        cameraDiagnostics(video, stream, { attempt, outcome: outcome.reason }),
      );

      detachStream(video);
      stopCamera(stream);
      stream = null;
    } catch (error) {
      const cameraError = error instanceof CameraError ? error : toCameraError(error);
      logCameraDiagnostics(
        'acquire_failed',
        cameraDiagnostics(video, stream, { attempt, outcome: cameraError.reason }),
      );

      detachStream(video);
      stopCamera(stream);
      stream = null;

      // A refusal or a missing camera will not be fixed by weaker constraints.
      if (cameraError.reason === 'DENIED' || cameraError.reason === 'INSECURE') throw cameraError;
      lastError = cameraError;
      if (cameraError.reason === 'NOT_FOUND' && !canRetryAttach(attempt, maxAttempts)) {
        throw cameraError;
      }
    }
  }

  throw lastError ?? new CameraError('NO_FRAMES');
}

/* --------------------------------------------------------------- capture */

export interface Capture {
  blob: Blob;
  /** Object URL for the preview. The caller revokes it. */
  previewUrl: string;
  capturedAt: string;
  width: number;
  height: number;
}

/**
 * Grabs the current frame and normalises it to JPEG.
 *
 * The long edge is capped at CAPTURE_MAX_EDGE and the aspect ratio is preserved —
 * the frame is scaled, never cropped, so a portrait photo stays portrait and the
 * report shows what the supervisor actually saw.
 */
export async function captureFrame(video: HTMLVideoElement): Promise<Capture> {
  const capturedAt = new Date().toISOString();

  const sourceWidth = video.videoWidth;
  const sourceHeight = video.videoHeight;
  if (!sourceWidth || !sourceHeight) throw new CameraError('NO_FRAMES');

  const scale = Math.min(1, CAPTURE_MAX_EDGE / Math.max(sourceWidth, sourceHeight));
  const width = Math.round(sourceWidth * scale);
  const height = Math.round(sourceHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) throw new CameraError('UNAVAILABLE');
  context.drawImage(video, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', CAPTURE_JPEG_QUALITY);
  });
  if (!blob) throw new CameraError('UNAVAILABLE');

  return { blob, previewUrl: URL.createObjectURL(blob), capturedAt, width, height };
}
